import { useState, useCallback } from 'react';
import { apiRequest } from '../../utils/apiUtils';
import { VenueBooking, BookingStatus } from '../../types/venue';

export interface BookingFilters {
  status?: BookingStatus;
  venueId?: string;
  dateFrom?: string;
  dateTo?: string;
}

export const useOwnerBookings = () => {
  const [bookings, setBookings] = useState<VenueBooking[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBookings = useCallback(async (filters?: BookingFilters) => {
    setLoading(true);
    setError(null);

    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.venueId) params.append('venueId', filters.venueId);
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);

    const endpoint = `/owner/bookings${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await apiRequest<VenueBooking[]>(endpoint);

    if (response.data) {
      setBookings(response.data);
    } else {
      setError(response.error || 'Error al cargar reservas');
    }

    setLoading(false);
  }, []);

  const confirmBooking = useCallback(
    async (bookingId: string) => {
      const response = await apiRequest<{ message: string; booking: VenueBooking }>(
        `/bookings/${bookingId}/confirm`,
        { method: 'POST' }
      );

      if (response.data) {
        // Update booking in state
        setBookings((prev) =>
          prev.map((b) => (b.id === bookingId ? response.data!.booking : b))
        );
        return { success: true, booking: response.data.booking };
      }

      return { success: false, error: response.error };
    },
    []
  );

  const completeBooking = useCallback(
    async (bookingId: string) => {
      const response = await apiRequest<{ message: string; booking: VenueBooking }>(
        `/owner/bookings/${bookingId}/complete`,
        { method: 'POST' }
      );

      if (response.data) {
        // Update booking in state
        setBookings((prev) =>
          prev.map((b) => (b.id === bookingId ? response.data!.booking : b))
        );
        return { success: true, booking: response.data.booking };
      }

      return { success: false, error: response.error };
    },
    []
  );

  return {
    bookings,
    loading,
    error,
    fetchBookings,
    confirmBooking,
    completeBooking,
  };
};
